/**
 * Only an app-internal path is ever followed after sign-in.
 *
 * `?redirect=` comes off a URL a stranger can write. Without this check it is
 * an open redirect: a link that signs a fan in and then drops them on a
 * look-alike page asking for the code they just used.
 *
 * This function must stand on its own. Do not assume the router sanitizes the
 * value. Today TanStack Router's `sanitizePath` strips control characters, and
 * `navigate` keeps only `.pathname` so a spoofed host is discarded. That is an
 * accident of two pinned versions, not this helper's contract. A control
 * character between the leading `/` and a `//` or `/\` pattern, or a
 * percent-encoded slash or backslash, must be refused here.
 *
 * Callers (none of them reload-based; none use `location.assign`):
 * - `apps/app/src/routes/sign-in.tsx` (`navigate({ href })` and
 *   `redirect({ href })`, both with `reloadDocument: false`)
 * - `apps/app/src/routes/sign-in_.link.tsx` (`navigate({ href })`,
 *   `reloadDocument: false`)
 * - `apps/app/src/routes/onboarding.tsx` (`navigate({ href })`, no reload)
 */
function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * `undefined` means the value could not be decoded, which is a refusal, not a
 * pass. `decodeURIComponent` throws for the WHOLE string if any percent
 * sequence anywhere in it is malformed, so returning the undecoded value on a
 * throw let one stray `%zz` switch the encoded-slash checks off entirely:
 * `safeRedirectPath("/%2F%2Fevil.example.com%zz")` came back unchanged, while
 * the same string without the trailing `%zz` was correctly refused.
 *
 * No caller decodes `%2F` into a literal `/` before treating the value as a
 * host, so this was not a live open redirect. It defeated the helper's stated
 * contract all the same, and that contract, not the router's current
 * behavior, is the thing this file exists to keep.
 */
function decodeOnce(value: string): string | undefined {
  try {
    return decodeURIComponent(value);
  } catch {
    return undefined;
  }
}

function isUnsafeRedirect(value: string): boolean {
  if (hasControlCharacter(value)) return true;
  if (!value.startsWith("/")) return true;
  if (value.startsWith("//") || value.startsWith("/\\")) return true;
  return false;
}

export function safeRedirectPath(candidate: string | undefined): string {
  if (candidate === undefined || candidate === "") return "/";
  if (isUnsafeRedirect(candidate)) return "/";
  const once = decodeOnce(candidate);
  if (once === undefined || isUnsafeRedirect(once)) return "/";
  const twice = decodeOnce(once);
  if (twice === undefined || isUnsafeRedirect(twice)) return "/";
  return candidate;
}
